import { createEvent, createStore, sample } from "effector";
import { combineEvents, reset } from "patronum";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import type { IUserAuthContract } from "@features/student/auth/model";
import type { ILessonsContract } from "@features/lessons/model";
import type { IEnrollmentsContract } from "@features/enrollments/model";
import type { IStreakContract } from "@features/streak/model";
import type { ITopicsContract } from "@features/topics/model";
import type { ICoursesContract } from "@features/courses/model";
import {
    ActiveLessonCompletion,
    LessonCompletionRequest,
    LessonCompletionResult,
    completionResultFromEnrollments,
    completionResultFromResponse,
    hasCompletionDetails,
    repeatedCompletionResult
} from "./result";

export type { LessonCompletionRequest, LessonCompletionResult } from "./result";

const lessonCompletionRequested = createEvent<LessonCompletionRequest>();
const lessonCompletionStarted = createEvent<ActiveLessonCompletion>();
const lessonCompletionSettled = createEvent<LessonCompletionResult>();
const lessonCompletionFailed = createEvent();
const enrollmentsRefreshAwaited = createEvent();

const $activeCompletion = createStore<ActiveLessonCompletion | null>(null)
    .on(lessonCompletionStarted, (_, completion) => completion)
    .reset(lessonCompletionFailed);

const $completionResult = createStore<LessonCompletionResult | null>(null)
    .on(lessonCompletionSettled, (_, result) => result)
    .reset(lessonCompletionStarted, lessonCompletionFailed);

const $isAwaitingEnrollmentsRefresh = createStore(false)
    .on(enrollmentsRefreshAwaited, () => true)
    .reset(lessonCompletionStarted, lessonCompletionSettled, lessonCompletionFailed);

sample({
    clock: lessonCompletionStarted,
    filter: (completion) => completion.wasCompleted,
    fn: repeatedCompletionResult,
    target: lessonCompletionSettled
});

const contract = new ModelContract(
    { $activeCompletion, $completionResult },
    { lessonCompletionRequested, lessonCompletionSettled, lessonCompletionFailed },
    {},
    {}
);

export type ILessonCompletionContract = typeof contract;

export class LessonCompletionModule extends ModuleDescription<ILessonCompletionContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public lessonsModule: ILessonsContract,
        public enrollmentsModule: IEnrollmentsContract,
        public streakModule: IStreakContract,
        public topicsModule: ITopicsContract,
        public coursesModule: ICoursesContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        const { lessonCompletedFx } = this.lessonsModule.effects;
        const { $enrollmentsCourses, $enrollmentsLessons } = this.enrollmentsModule.stores;
        const { $streakStats } = this.streakModule.stores;

        sample({
            clock: lessonCompletionRequested,
            source: {
                active: $activeCompletion,
                result: $completionResult,
                enrollmentsLessons: $enrollmentsLessons,
                streakStats: $streakStats
            },
            filter: ({ active, result }, request) =>
                active === null || active.lessonId !== request.lessonId || result !== null,
            fn: ({ enrollmentsLessons, streakStats }, request): ActiveLessonCompletion => ({
                ...request,
                wasCompleted: enrollmentsLessons.some(
                    (enrollment) =>
                        enrollment.lesson_id === request.lessonId &&
                        enrollment.status === UserEnrollmentStatus.Completed
                ),
                streakBefore: streakStats
            }),
            target: lessonCompletionStarted
        });

        sample({
            clock: lessonCompletionStarted,
            filter: (completion) => !completion.wasCompleted,
            fn: ({ topicId, lessonId }) => ({ topicId, lessonId }),
            target: this.lessonsModule.events.completeLesson
        });

        const activeLessonCompleted = sample({
            clock: lessonCompletedFx.done,
            source: $activeCompletion,
            filter: (active, { params }) => active !== null && active.lessonId === params.lessonId,
            fn: (active, { result }) => ({ completion: active!, response: result })
        });

        sample({
            clock: activeLessonCompleted,
            filter: ({ response }) => hasCompletionDetails(response),
            fn: ({ completion, response }) => completionResultFromResponse(completion, response),
            target: lessonCompletionSettled
        });

        sample({
            clock: activeLessonCompleted,
            filter: ({ response }) => response.streak !== undefined,
            fn: ({ response }) => response.streak!,
            target: this.streakModule.events.statsChanged
        });

        sample({
            clock: activeLessonCompleted,
            filter: ({ response }) => !hasCompletionDetails(response),
            target: enrollmentsRefreshAwaited
        });

        const enrollmentsRefreshed = combineEvents({
            events: [
                this.enrollmentsModule.effects.getEnrollmentsCoursesFx.finally,
                this.enrollmentsModule.effects.getEnrollmentsLessonsFx.finally,
                this.streakModule.effects.getStreakStatsFx.finally
            ],
            reset: lessonCompletedFx.done
        });

        sample({
            clock: enrollmentsRefreshed,
            source: {
                isAwaiting: $isAwaitingEnrollmentsRefresh,
                completion: $activeCompletion,
                enrollmentsCourses: $enrollmentsCourses,
                enrollmentsLessons: $enrollmentsLessons,
                lessons: this.lessonsModule.stores.$lessons,
                streakStats: $streakStats
            },
            filter: ({ isAwaiting, completion }) => isAwaiting && completion !== null,
            fn: ({ completion, ...stores }) => completionResultFromEnrollments(completion!, stores),
            target: lessonCompletionSettled
        });

        sample({
            clock: lessonCompletedFx.fail,
            source: $activeCompletion,
            filter: (active, { params }) => active !== null && active.lessonId === params.lessonId,
            target: lessonCompletionFailed
        });

        sample({
            clock: lessonCompletionSettled,
            filter: (result) => result.isTopicCompleted,
            fn: ({ courseId, topicId }) => ({ courseId, topicId }),
            target: this.topicsModule.events.completeTopic
        });

        sample({
            clock: lessonCompletionSettled,
            filter: (result) => result.isCourseCompleted,
            fn: ({ courseId }) => ({ courseId }),
            target: this.coursesModule.events.completeCourse
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [$activeCompletion, $completionResult, $isAwaitingEnrollmentsRefresh]
        });
    }
}
